import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Film, Users, Key, UserPlus, ArrowRight, ArrowLeft, Check, SkipForward, Eye, EyeOff, ExternalLink, Sparkles } from 'lucide-react';

const STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'team', label: 'Team' },
  { id: 'keys', label: 'API Keys' },
  { id: 'invite', label: 'Invite' },
];

const PROVIDER_GROUPS = [
  {
    category: 'AI Generation',
    providers: [
      { key: 'openai_api_key', label: 'OpenAI', desc: 'Image generation (GPT Image 1)', url: 'https://platform.openai.com/api-keys' },
      { key: 'gemini_api_key', label: 'Google Gemini', desc: 'Text & image generation', url: 'https://aistudio.google.com/apikey' },
    ]
  },
  {
    category: 'Video Generation',
    providers: [
      { key: 'kling_api_key', label: 'Kling', desc: 'Kling video generation', url: 'https://kling.ai/dev/api-key' },
      { key: 'google_ai_api_key', label: 'Google AI (Veo)', desc: 'Veo video generation', url: 'https://aistudio.google.com/apikey' },
      { key: 'seedance_api_key', label: 'Seedance (ByteDance)', desc: 'Seedance video gen', url: 'https://seedance.ai' },
    ]
  },
  {
    category: 'Audio',
    providers: [
      { key: 'elevenlabs_api_key', label: 'ElevenLabs', desc: 'Voice / TTS generation', url: 'https://elevenlabs.io' },
    ]
  }
];

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState(null);
  const [apiKeys, setApiKeys] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [inviteEmails, setInviteEmails] = useState(['']);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Enter a team name');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/teams', { name: teamName.trim() });
      setTeamId(res.data.id);
      toast.success('Team created!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipTeam = async () => {
    // Use personal team
    try {
      const res = await api.get('/teams');
      const personal = res.data.find(t => t.is_personal);
      if (personal) setTeamId(personal.id);
    } catch {}
    setStep(2);
  };

  const handleSaveKeys = async () => {
    const activeTeamId = teamId;
    if (!activeTeamId) {
      setStep(3);
      return;
    }
    const keysToSave = {};
    Object.entries(apiKeys).forEach(([k, v]) => {
      if (v && v.trim()) keysToSave[k] = v.trim();
    });
    if (Object.keys(keysToSave).length === 0) {
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      await api.put(`/teams/${activeTeamId}/api-keys`, keysToSave);
      toast.success('API keys saved!');
      setStep(3);
    } catch (err) {
      toast.error('Failed to save keys');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    const validEmails = inviteEmails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length > 0 && teamId) {
      setLoading(true);
      let sent = 0;
      for (const email of validEmails) {
        try {
          await api.post(`/teams/${teamId}/invite`, { email: email.trim() });
          sent++;
        } catch {}
      }
      if (sent > 0) toast.success(`${sent} invite(s) sent!`);
      setLoading(false);
    }
    await finishOnboarding();
  };

  const finishOnboarding = async () => {
    try {
      await api.put('/auth/onboarding', { completed: true });
      if (refreshUser) await refreshUser();
      navigate('/dashboard');
    } catch {
      navigate('/dashboard');
    }
  };

  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center px-4" data-testid="onboarding-page">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#121216] z-50">
        <div
          className="h-full bg-[#FF4500] transition-all duration-500 ease-out"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="w-full max-w-xl">
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < step ? 'bg-[#FF4500] text-white' :
                i === step ? 'bg-[#FF4500]/20 text-[#FF4500] ring-2 ring-[#FF4500]/40' :
                'bg-[#121216] text-[#55555C]'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 ${i < step ? 'bg-[#FF4500]' : 'bg-[#1A1A20]'}`} />
              )}
            </div>
          ))}
        </div>

        {/* STEP 0: Welcome */}
        {currentStep.id === 'welcome' && (
          <div className="text-center" data-testid="onboarding-welcome">
            <div className="w-16 h-16 bg-[#FF4500] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Film className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to AvantGarde
            </h1>
            <p className="text-[#8A8A93] text-lg mb-10 max-w-md mx-auto">
              Let's get you set up. You can create a team workspace or jump straight in with your personal workspace.
            </p>
            <button
              data-testid="onboarding-get-started-btn"
              onClick={() => setStep(1)}
              className="ff-btn-primary px-8 py-3 text-base flex items-center gap-2 mx-auto"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* STEP 1: Create Team */}
        {currentStep.id === 'team' && (
          <div data-testid="onboarding-team">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[#FF4500]" />
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Create a Team
              </h2>
            </div>
            <p className="text-[#8A8A93] mb-8">
              Teams let you collaborate with others and share API keys across projects.
            </p>
            <div className="mb-6">
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Team Name</label>
              <input
                data-testid="team-name-input"
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="ff-input w-full"
                placeholder="e.g. Production Studio"
              />
            </div>
            <div className="flex gap-3">
              <button
                data-testid="create-team-btn"
                onClick={handleCreateTeam}
                disabled={loading}
                className="ff-btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>Create Team <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button
                data-testid="skip-team-btn"
                onClick={handleSkipTeam}
                className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8A8A93] text-sm font-medium transition-colors flex items-center gap-2"
              >
                <SkipForward className="w-4 h-4" /> Skip
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: API Keys */}
        {currentStep.id === 'keys' && (
          <div data-testid="onboarding-keys">
            <div className="flex items-center gap-3 mb-2">
              <Key className="w-6 h-6 text-[#FF4500]" />
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Add API Keys
              </h2>
            </div>
            <p className="text-[#8A8A93] mb-6 text-sm">
              Connect your AI service providers. You can add or change these anytime in Settings.
            </p>

            <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-1">
              {PROVIDER_GROUPS.map(group => (
                <div key={group.category}>
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] mb-3">{group.category}</h3>
                  <div className="space-y-3">
                    {group.providers.map(p => (
                      <div key={p.key} className="bg-[#0E0E12] border border-white/[0.06] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-sm font-medium text-white">{p.label}</span>
                            <span className="text-xs text-[#55555C] ml-2">{p.desc}</span>
                          </div>
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#55555C] hover:text-[#FF4500] transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                        <div className="relative">
                          <input
                            data-testid={`api-key-input-${p.key}`}
                            type={showKeys[p.key] ? 'text' : 'password'}
                            value={apiKeys[p.key] || ''}
                            onChange={(e) => setApiKeys(prev => ({ ...prev, [p.key]: e.target.value }))}
                            className="ff-input w-full pr-10 text-sm"
                            placeholder={`Paste your ${p.label} key`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowKeys(prev => ({ ...prev, [p.key]: !prev[p.key] }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white transition-colors"
                          >
                            {showKeys[p.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8A8A93] text-sm font-medium transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                data-testid="save-keys-btn"
                onClick={handleSaveKeys}
                disabled={loading}
                className="ff-btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button onClick={() => setStep(3)} className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8A8A93] text-sm font-medium transition-colors flex items-center gap-2">
                <SkipForward className="w-4 h-4" /> Skip
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Invite */}
        {currentStep.id === 'invite' && (
          <div data-testid="onboarding-invite">
            <div className="flex items-center gap-3 mb-2">
              <UserPlus className="w-6 h-6 text-[#FF4500]" />
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Invite Your Team
              </h2>
            </div>
            <p className="text-[#8A8A93] mb-6 text-sm">
              Add collaborators by email. They'll get an invitation to join your team.
            </p>

            <div className="space-y-3 mb-6">
              {inviteEmails.map((email, i) => (
                <input
                  key={i}
                  data-testid={`invite-email-${i}`}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const copy = [...inviteEmails];
                    copy[i] = e.target.value;
                    setInviteEmails(copy);
                  }}
                  className="ff-input w-full"
                  placeholder="colleague@example.com"
                />
              ))}
              {inviteEmails.length < 5 && (
                <button
                  onClick={() => setInviteEmails([...inviteEmails, ''])}
                  className="text-sm text-[#FF4500] hover:text-[#FF5722] font-medium transition-colors"
                >
                  + Add another
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8A8A93] text-sm font-medium transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                data-testid="finish-onboarding-btn"
                onClick={handleInvite}
                disabled={loading}
                className="ff-btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Sparkles className="w-4 h-4" /> Start Creating</>}
              </button>
              <button
                data-testid="skip-invite-btn"
                onClick={finishOnboarding}
                className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[#8A8A93] text-sm font-medium transition-colors flex items-center gap-2"
              >
                <SkipForward className="w-4 h-4" /> Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
